/**
 * TreeNodeWrapper - Generic tree structure renderer.
 *
 * Responsibilities:
 * - Render tree visual structure (indents, connector lines)
 * - Render collapse/expand button
 * - Handle selection state and click events
 *
 * Does NOT know about:
 * - Domain-specific content (that's passed as children)
 * - What kind of data is being displayed
 *
 * This component wraps arbitrary content, making it reusable for any tree view.
 */

import { type ReactNode } from "react";
import { Button } from "@/src/components/ui/button";
import { ItemBadge, type LangfuseItemType } from "@/src/components/ItemBadge";
import { ChevronRight } from "lucide-react";
import { cn } from "@/src/utils/tailwind";

export interface TreeNodeMetadata {
  depth: number;
  treeLines: boolean[];
  isLastSibling: boolean;
  /**
   * Deepest level to render indentation for; nodes beyond it render flat at
   * this level so extremely deep trees never push content off-viewport
   * (LFE-10959). Defaults to unbounded for callers that don't measure.
   */
  maxVisualDepth?: number;
}

interface TreeNodeWrapperProps {
  // Tree structure data
  metadata: TreeNodeMetadata;
  nodeType: LangfuseItemType; // For the icon badge (e.g., "SPAN", "GENERATION", "TRACE")
  hasChildren: boolean;
  isCollapsed: boolean;
  onToggleCollapse: () => void;

  // Selection/interaction
  isSelected: boolean;
  onSelect: () => void;

  // Content to render (fully decoupled from tree structure)
  children: ReactNode;

  // Optional customization
  className?: string;
}

export function VirtualizedTreeNodeWrapper({
  metadata,
  nodeType,
  hasChildren,
  isCollapsed,
  onToggleCollapse,
  isSelected,
  onSelect,
  children,
  className,
}: TreeNodeWrapperProps) {
  const { depth, treeLines, isLastSibling } = metadata;
  const maxVisualDepth = metadata.maxVisualDepth ?? Infinity;
  // Visual depth: real depth, capped so indentation never exceeds the
  // container. Rows past the cap render flat at the cap level.
  const visualDepth = Math.min(depth, maxVisualDepth);
  // A capped node's children render at the SAME indent (not one level right),
  // so its child spine would dangle next to nothing — suppress it.
  const childrenAreCapped = depth >= maxVisualDepth;

  return (
    <div
      className={cn(
        "relative flex w-full cursor-pointer px-0",
        // Dim unselected rows in dark only — in light the gray read as washed
        // out, an accepted light/dark inconsistency.
        isSelected
          ? "bg-muted text-foreground"
          : "hover:bg-muted/50 dark:text-muted-foreground",
        className,
      )}
      style={{
        paddingTop: 0,
        paddingBottom: 0,
      }}
      onClick={(e) => {
        if (!e.currentTarget?.closest("[data-expand-button]")) {
          onSelect();
        }
      }}
    >
      <div className="flex w-full pl-2">
        {/* 1. Indents: ancestor level indicators (capped at visualDepth) */}
        {visualDepth > 0 && (
          <div className="flex shrink-0">
            {/* Math.max floors the length at 0 so a stray non-positive/NaN depth
                can never reach Array.from as a value that throws RangeError. */}
            {Array.from({ length: Math.max(0, visualDepth - 1) }, (_, i) => (
              <div key={i} className="relative w-5">
                {treeLines[i] && (
                  <div className="bg-border-contrast absolute top-0 bottom-0 left-3 w-px" />
                )}
              </div>
            ))}
          </div>
        )}

        {/* 2. Current element bars: up/down/horizontal connectors */}
        {visualDepth > 0 && (
          <div className="relative w-5 shrink-0">
            <>
              {/* Vertical bar connecting upwards */}
              <div
                className={cn(
                  "bg-border-contrast absolute top-0 left-3 w-px",
                  isLastSibling ? "h-3" : "bottom-3",
                )}
              />
              {/* Vertical bar connecting downwards if not last sibling */}
              {!isLastSibling && (
                <div className="bg-border-contrast absolute top-3 bottom-0 left-3 w-px" />
              )}
              {/* Horizontal bar connecting to icon */}
              <div className="bg-border-contrast absolute top-3 left-3 h-px w-2" />
            </>
          </div>
        )}

        {/* 3. Icon + child connector: fixed width container */}
        <div className="relative flex w-6 shrink-0 flex-col py-1.5">
          <div className="relative z-10 flex h-4 items-center justify-center">
            <ItemBadge type={nodeType} isSmall className="size-3!" />
          </div>
          {/* Vertical bar downwards if there are expanded children (skipped
              when children render capped at this same indent — the spine
              would point at nothing) */}
          {hasChildren && !isCollapsed && !childrenAreCapped && (
            <div className="bg-border-contrast absolute top-3 bottom-0 left-1/2 w-px" />
          )}
          {/* Root node downward connector */}
          {depth === 0 && hasChildren && !isCollapsed && !childrenAreCapped && (
            <div className="bg-border-contrast absolute top-3 bottom-0 left-1/2 w-px" />
          )}
        </div>

        {/* 4. Content area (passed as children - completely decoupled) */}
        <div className="flex min-w-0 flex-1">{children}</div>

        {/* 5. Expand/Collapse button */}
        {hasChildren && (
          <div className="flex items-center justify-end py-1 pr-1">
            <Button
              aria-expanded={!isCollapsed}
              data-expand-button
              size="icon"
              variant="ghost"
              onClick={(ev) => {
                ev.stopPropagation();
                onToggleCollapse();
              }}
              className="hover:bg-primary/10 h-6 w-6 shrink-0"
            >
              <span
                className={cn(
                  "inline-block h-4 w-4 transform transition-transform duration-200 ease-in-out",
                  isCollapsed ? "rotate-0" : "rotate-90",
                )}
              >
                <ChevronRight className="h-4 w-4" />
              </span>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
